import { useParams } from "react-router-dom";
import { PaginatedResponse } from "../../Lib/responses";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { Endpoints } from "../../Lib/endpoints";
import { StudentLifeHistoryDataProps } from "../../Lib/types";
import {
  Container,
  Text,
  Card,
  Group,
  Stack,
  Avatar,
  Badge,
  Tabs,
  SimpleGrid,
  Progress,
  RingProgress,
  Divider,
  Paper,
  Skeleton,
  ThemeIcon,
  Grid,
  Accordion,
  Timeline,
  Chip,
  ActionIcon,
  Tooltip,
  NumberFormatter,
} from "@mantine/core";
import {
  IconUser,
  IconMail,
  IconMapPin,
  IconCalendar,
  IconCreditCard,
  IconBook,
  IconTrophy,
  IconChartBar,
  IconReceipt,
  IconTestPipe,
  IconUsers,
  IconSchool,
  IconClock,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconEye,
  IconDownload,
  IconShare,
  IconStar,
  IconTrendingUp,
  IconTrendingDown,
  IconCurrencyDollar,
  IconCalendarTime,
  IconLocation,
  IconPhone,
  IconShield,
  IconLock,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { getFinancialValueFromNumeric } from "../../Lib/methods";
import MegaLoader from "../../Components/MegaLoader";

dayjs.extend(relativeTime);

export default function SingleStudent() {
  const { studentID } = useParams();
  const {
    data: lifeHistory,
    isLoading: isLoadingLifeHistory,
    reloadData: reloadLifeHistory,
  } = usePerformRequest<StudentLifeHistoryDataProps>({
    method: Endpoints.GetStudentLifeHistory(studentID ?? "").method,
    route: Endpoints.GetStudentLifeHistory(studentID ?? "").route,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
      case "success":
      case "successful":
        return "green";
      case "pending":
        return "yellow";
      case "failed":
      case "canceled":
      case "past_due":
      case "unpaid":
      case "inactive":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
      case "success":
      case "successful":
        return <IconCheck size={16} />;
      case "pending":
        return <IconClock size={16} />;
      case "failed":
      case "canceled":
      case "past_due":
      case "unpaid":
      case "inactive":
        return <IconX size={16} />;
      default:
        return <IconAlertTriangle size={16} />;
    }
  };

  const StatCard = ({ title, value, icon, color, description, trend }: any) => (
    <Card shadow="sm" padding="xl" radius="md" withBorder>
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm" fw={500} tt="uppercase">
            {title}
          </Text>
          {isLoadingLifeHistory ? (
            <Skeleton height={24} width={80} my={4} />
          ) : (
            <Text fw={700} size="xl">
              {title.includes("Spent") || title.includes("Amount") ? (
                <NumberFormatter value={value} thousandSeparator prefix="$" />
              ) : title.includes("Score") ? (
                `${value}%`
              ) : (
                <NumberFormatter value={value} thousandSeparator />
              )}
            </Text>
          )}
          {description && (
            <Text c="dimmed" size="xs" mt={4}>
              {description}
            </Text>
          )}
          {trend && (
            <Group gap={4} mt={4}>
              {trend > 0 ? (
                <IconTrendingUp size={12} color="green" />
              ) : (
                <IconTrendingDown size={12} color="red" />
              )}
              <Text size="xs" c={trend > 0 ? "green" : "red"}>
                {Math.abs(trend)}% from last month
              </Text>
            </Group>
          )}
        </div>
        <ThemeIcon color={color} size={60} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );

  if (isLoadingLifeHistory) {
    return (
      <div
        style={{ padding: "32px", background: "#f8f9fa", minHeight: "100vh" }}
      >
        <Container size="xl">
          <Stack gap="xl">
            <Skeleton height={60} width={300} />
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} height={120} radius="md" />
              ))}
            </SimpleGrid>
            <Skeleton height={400} radius="md" />
          </Stack>
        </Container>
      </div>
    );
  }

  if (!lifeHistory) {
    return (
      <div
        style={{ padding: "32px", background: "#f8f9fa", minHeight: "100vh" }}
      >
        <Container size="xl">
          <Paper
            style={{
              textAlign: "center",
              padding: "48px 24px",
              background: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: 12,
            }}
          >
            <IconUser size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
            <Text size="lg" fw={500} mb={8}>
              Student not found
            </Text>
            <Text size="sm" c="dimmed">
              The student you're looking for doesn't exist or has been removed.
            </Text>
          </Paper>
        </Container>
      </div>
    );
  }

  const {
    student,
    statistics,
    subscriptions,
    transactions,
    assessments,
    attempts,
    enrolledPlans,
    courses,
    batches,
  } = lifeHistory;

  return (
    <div style={{ padding: "32px", background: "#f8f9fa", minHeight: "100vh" }}>
      {isLoadingLifeHistory ? (
        <MegaLoader />
      ) : (
        <>
          {lifeHistory && lifeHistory.student && lifeHistory.student.id ? (
            <Container size="xl">
              {/* Header */}
              <Group justify="space-between" align="flex-start" mb={32}>
                <Group gap="lg">
                  <Avatar
                    size={80}
                    radius="xl"
                    style={{ border: "4px solid #e9ecef" }}
                  >
                    {student.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                  <div>
                    <Text size="xl" fw={700} mb={4}>
                      {student.name}
                    </Text>
                    <Group gap="md" mb={8}>
                      <Badge
                        color={student.isActive ? "green" : "red"}
                        variant="light"
                        leftSection={
                          student.isActive ? (
                            <IconCheck size={12} />
                          ) : (
                            <IconX size={12} />
                          )
                        }
                      >
                        {student.isActive ? "Active" : "Inactive"}
                      </Badge>
                      <Badge
                        color={student.isEmailVerified ? "green" : "yellow"}
                        variant="light"
                        leftSection={
                          student.isEmailVerified ? (
                            <IconCheck size={12} />
                          ) : (
                            <IconMail size={12} />
                          )
                        }
                      >
                        {student.isEmailVerified
                          ? "Email Verified"
                          : "Email Unverified"}
                      </Badge>
                      <Badge
                        color={student.isPhoneVerified ? "green" : "yellow"}
                        variant="light"
                        leftSection={
                          student.isPhoneVerified ? (
                            <IconCheck size={12} />
                          ) : (
                            <IconPhone size={12} />
                          )
                        }
                      >
                        {student.isPhoneVerified
                          ? "Phone Verified"
                          : "Phone Unverified"}
                      </Badge>
                    </Group>
                    <Group gap="lg" c="dimmed">
                      <Group gap={4}>
                        <IconMail size={14} />
                        <Text>{student.email}</Text>
                      </Group>
                      <Group gap={4}>
                        <IconMapPin size={14} />
                        <Text>
                          {student.address.city}, {student.address.country}
                        </Text>
                      </Group>
                      <Group gap={4}>
                        <IconCalendar size={14} />
                        <Text>
                          Joined {dayjs(student.createdAt).format("MMM YYYY")}
                        </Text>
                      </Group>
                    </Group>
                  </div>
                </Group>
                <Group gap={8}>
                  <ActionIcon variant="outline" size="lg" color="blue">
                    <IconEye size={20} />
                  </ActionIcon>
                  <ActionIcon variant="outline" size="lg" color="green">
                    <IconDownload size={20} />
                  </ActionIcon>
                  <ActionIcon variant="outline" size="lg" color="violet">
                    <IconShare size={20} />
                  </ActionIcon>
                </Group>
              </Group>

              {/* Statistics Cards */}
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" mb={32}>
                <StatCard
                  title="Total Subscriptions"
                  value={statistics.totalSubscriptions}
                  icon={<IconCreditCard size={30} />}
                  color="blue"
                  description="All time subscriptions"
                />
                <StatCard
                  title="Active Subscriptions"
                  value={statistics.activeSubscriptions}
                  icon={<IconCheck size={30} />}
                  color="green"
                  description="Currently active"
                />
                <StatCard
                  title="Total Spent"
                  value={statistics.totalSpent}
                  icon={<IconCurrencyDollar size={30} />}
                  color="violet"
                  description="Lifetime spending"
                />
                <StatCard
                  title="Average Score"
                  value={statistics.averageScore}
                  icon={<IconTrophy size={30} />}
                  color="yellow"
                  description="Assessment performance"
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" mb={32}>
                <StatCard
                  title="Total Transactions"
                  value={statistics.totalTransactions}
                  icon={<IconReceipt size={30} />}
                  color="indigo"
                  description="Payment history"
                />
                <StatCard
                  title="Successful Transactions"
                  value={statistics.successfulTransactions}
                  icon={<IconCheck size={30} />}
                  color="green"
                  description="Completed payments"
                />
                <StatCard
                  title="Plans Enrolled"
                  value={statistics.totalPlansEnrolled}
                  icon={<IconBook size={30} />}
                  color="orange"
                  description="Subscribed plans"
                />
                <StatCard
                  title="Courses Enrolled"
                  value={statistics.totalCoursesEnrolled}
                  icon={<IconSchool size={30} />}
                  color="teal"
                  description="Enrolled courses"
                />
              </SimpleGrid>

              {/* Main Content Tabs */}
              <Tabs defaultValue="overview" mb={24}>
                <Tabs.List grow>
                  <Tabs.Tab
                    value="overview"
                    leftSection={<IconChartBar size={16} />}
                  >
                    Overview
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="subscriptions"
                    leftSection={<IconCreditCard size={16} />}
                  >
                    Subscriptions ({subscriptions.length})
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="transactions"
                    leftSection={<IconReceipt size={16} />}
                  >
                    Transactions ({transactions.length})
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="assessments"
                    leftSection={<IconTestPipe size={16} />}
                  >
                    Assessments ({assessments.length})
                  </Tabs.Tab>
                  <Tabs.Tab
                    value="courses"
                    leftSection={<IconSchool size={16} />}
                  >
                    Courses ({courses.length})
                  </Tabs.Tab>
                </Tabs.List>

                {/* Overview Tab */}
                <Tabs.Panel value="overview" pt="lg">
                  <Grid gutter="lg">
                    <Grid.Col span={{ base: 12, lg: 8 }}>
                      <Card
                        shadow="sm"
                        padding="xl"
                        radius="md"
                        withBorder
                        mb="lg"
                      >
                        <Text fw={600} mb="lg">
                          Performance Overview
                        </Text>
                        <SimpleGrid cols={2} spacing="lg">
                          <div>
                            <Text size="sm" c="dimmed" mb={8}>
                              Assessment Success Rate
                            </Text>
                            <RingProgress
                              size={120}
                              thickness={12}
                              sections={[
                                {
                                  value:
                                    (statistics.successfulTransactions /
                                      statistics.totalTransactions) *
                                    100,
                                  color: "green",
                                },
                              ]}
                              label={
                                <Text ta="center" fw={700} size="lg">
                                  {Math.round(
                                    (statistics.successfulTransactions /
                                      statistics.totalTransactions) *
                                      100
                                  )}
                                  %
                                </Text>
                              }
                            />
                          </div>
                          <div>
                            <Text size="sm" c="dimmed" mb={8}>
                              Learning Progress
                            </Text>
                            <RingProgress
                              size={120}
                              thickness={12}
                              sections={[
                                {
                                  value:
                                    (statistics.totalAssessmentsTaken /
                                      Math.max(
                                        statistics.totalCoursesEnrolled * 5,
                                        1
                                      )) *
                                    100,
                                  color: "blue",
                                },
                              ]}
                              label={
                                <Text ta="center" fw={700} size="lg">
                                  {Math.round(
                                    (statistics.totalAssessmentsTaken /
                                      Math.max(
                                        statistics.totalCoursesEnrolled * 5,
                                        1
                                      )) *
                                      100
                                  )}
                                  %
                                </Text>
                              }
                            />
                          </div>
                        </SimpleGrid>
                      </Card>

                      <Card shadow="sm" padding="xl" radius="md" withBorder>
                        <Text fw={600} mb="lg">
                          Recent Activity
                        </Text>
                        <Timeline active={2} bulletSize={24} lineWidth={2}>
                          {transactions
                            .slice(0, 5)
                            .map((transaction, index) => (
                              <Timeline.Item
                                key={transaction.id}
                                bullet={<IconReceipt size={12} />}
                                title={`${
                                  transaction.type === "new" ? "New" : "Renewal"
                                } Subscription`}
                              >
                                <Text size="sm" c="dimmed" mb={4}>
                                  {dayjs(transaction.createdAt).format(
                                    "MMM DD, YYYY"
                                  )}
                                </Text>
                                <Text size="sm">
                                  $
                                  {getFinancialValueFromNumeric(
                                    transaction.amount
                                  )}{" "}
                                  via {transaction.provider}
                                </Text>
                                <Badge
                                  color={getStatusColor(transaction.status)}
                                  variant="light"
                                  size="xs"
                                  mt={4}
                                >
                                  {transaction.status}
                                </Badge>
                              </Timeline.Item>
                            ))}
                        </Timeline>
                      </Card>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, lg: 4 }}>
                      <Card
                        shadow="sm"
                        padding="xl"
                        radius="md"
                        withBorder
                        mb="lg"
                      >
                        <Text fw={600} mb="lg">
                          Account Status
                        </Text>
                        <Stack gap="md">
                          <Group justify="space-between">
                            <Group gap={8}>
                              <IconShield size={16} />
                              <Text size="sm">Account Status</Text>
                            </Group>
                            <Badge
                              color={
                                student.isAccountDeleted
                                  ? "red"
                                  : student.isAccountSuspended
                                  ? "yellow"
                                  : "green"
                              }
                              variant="light"
                            >
                              {student.isAccountDeleted
                                ? "Deleted"
                                : student.isAccountSuspended
                                ? "Suspended"
                                : "Active"}
                            </Badge>
                          </Group>
                          <Group justify="space-between">
                            <Group gap={8}>
                              <IconLock size={16} />
                              <Text size="sm">Login Method</Text>
                            </Group>
                            <Badge variant="light" color="blue">
                              {student.login_method.replace("_", " ")}
                            </Badge>
                          </Group>
                          <Group justify="space-between">
                            <Group gap={8}>
                              <IconCalendar size={16} />
                              <Text size="sm">Member Since</Text>
                            </Group>
                            <Text size="sm" fw={500}>
                              {dayjs(student.createdAt).format("MMM YYYY")}
                            </Text>
                          </Group>
                          <Group justify="space-between">
                            <Group gap={8}>
                              <IconLocation size={16} />
                              <Text size="sm">Location</Text>
                            </Group>
                            <Text size="sm" fw={500}>
                              {student.address.city}, {student.address.country}
                            </Text>
                          </Group>
                        </Stack>
                      </Card>

                      <Card shadow="sm" padding="xl" radius="md" withBorder>
                        <Text fw={600} mb="lg">
                          Enrolled Plans
                        </Text>
                        <Stack gap="md">
                          {enrolledPlans.map((plan) => (
                            <Paper key={plan.id} p="md" withBorder>
                              <Group justify="space-between">
                                <Text fw={500} size="sm">
                                  {plan.name}
                                </Text>
                                <IconBook size={16} color="#228be6" />
                              </Group>
                            </Paper>
                          ))}
                          {enrolledPlans.length === 0 && (
                            <Text size="sm" c="dimmed" ta="center">
                              No plans enrolled yet
                            </Text>
                          )}
                        </Stack>
                      </Card>
                    </Grid.Col>
                  </Grid>
                </Tabs.Panel>

                {/* Subscriptions Tab */}
                <Tabs.Panel value="subscriptions" pt="lg">
                  <Card shadow="sm" padding="xl" radius="md" withBorder>
                    <Text fw={600} mb="lg">
                      Subscription History
                    </Text>
                    <Stack gap="md">
                      {subscriptions.map((subscription) => (
                        <Paper key={subscription.id} p="lg" withBorder>
                          <Group justify="space-between" align="flex-start">
                            <div>
                              <Text fw={600} mb={4}>
                                Plan ID: {subscription.planID}
                              </Text>
                              <Group gap="lg" c="dimmed">
                                <Group gap={4}>
                                  <IconCalendar size={14} />
                                  <Text>
                                    Created:{" "}
                                    {dayjs(subscription.createdAt).format(
                                      "MMM DD, YYYY"
                                    )}
                                  </Text>
                                </Group>
                                <Group gap={4}>
                                  <IconClock size={14} />
                                  <Text>
                                    Expires:{" "}
                                    {dayjs(subscription.expiresAt).format(
                                      "MMM DD, YYYY"
                                    )}
                                  </Text>
                                </Group>
                              </Group>
                            </div>
                            <Group gap={8}>
                              <Badge
                                color={getStatusColor(subscription.status)}
                                variant="light"
                                leftSection={getStatusIcon(subscription.status)}
                              >
                                {subscription.status}
                              </Badge>
                              <Badge variant="light" color="blue">
                                {subscription.provider}
                              </Badge>
                            </Group>
                          </Group>
                          <Divider my="md" />
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                              Period:{" "}
                              {dayjs(subscription.currentPeriodStart).format(
                                "MMM DD"
                              )}{" "}
                              -{" "}
                              {dayjs(subscription.currentPeriodEnd).format(
                                "MMM DD, YYYY"
                              )}
                            </Text>
                            <Chip
                              size="xs"
                              variant={
                                subscription.cancelAtPeriodEnd
                                  ? "filled"
                                  : "light"
                              }
                              color={
                                subscription.cancelAtPeriodEnd ? "red" : "green"
                              }
                            >
                              {subscription.cancelAtPeriodEnd
                                ? "Cancels at period end"
                                : "Auto-renewal"}
                            </Chip>
                          </Group>
                        </Paper>
                      ))}
                      {subscriptions.length === 0 && (
                        <Paper
                          style={{
                            textAlign: "center",
                            padding: "48px 24px",
                            background: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: 12,
                          }}
                        >
                          <IconCreditCard
                            size={64}
                            style={{ marginBottom: 16, opacity: 0.5 }}
                          />
                          <Text size="lg" fw={500} mb={8}>
                            No subscriptions found
                          </Text>
                          <Text size="sm" c="dimmed">
                            This student hasn't subscribed to any plans yet.
                          </Text>
                        </Paper>
                      )}
                    </Stack>
                  </Card>
                </Tabs.Panel>

                {/* Transactions Tab */}
                <Tabs.Panel value="transactions" pt="lg">
                  <Card shadow="sm" padding="xl" radius="md" withBorder>
                    <Text fw={600} mb="lg">
                      Transaction History
                    </Text>
                    <Stack gap="md">
                      {transactions.map((transaction) => (
                        <Paper key={transaction.id} p="lg" withBorder>
                          <Group justify="space-between" align="flex-start">
                            <div>
                              <Text fw={600} mb={4}>
                                {transaction.reference}
                              </Text>
                              <Group gap="lg" c="dimmed">
                                <Group gap={4}>
                                  <IconCalendar size={14} />
                                  <Text>
                                    {dayjs(transaction.createdAt).format(
                                      "MMM DD, YYYY HH:mm"
                                    )}
                                  </Text>
                                </Group>
                                <Group gap={4}>
                                  <IconCreditCard size={14} />
                                  <Text>Plan ID: {transaction.planID}</Text>
                                </Group>
                              </Group>
                            </div>
                            <Group gap={8} align="flex-start">
                              <Text fw={700} size="lg" c="green">
                                $
                                {getFinancialValueFromNumeric(
                                  transaction.amount
                                )}
                              </Text>
                              <Stack gap={4}>
                                <Badge
                                  color={getStatusColor(transaction.status)}
                                  variant="light"
                                  leftSection={getStatusIcon(
                                    transaction.status
                                  )}
                                >
                                  {transaction.status}
                                </Badge>
                                <Badge variant="light" color="blue">
                                  {transaction.provider}
                                </Badge>
                                <Badge variant="light" color="violet">
                                  {transaction.type}
                                </Badge>
                              </Stack>
                            </Group>
                          </Group>
                        </Paper>
                      ))}
                      {transactions.length === 0 && (
                        <Paper
                          style={{
                            textAlign: "center",
                            padding: "48px 24px",
                            background: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: 12,
                          }}
                        >
                          <IconReceipt
                            size={64}
                            style={{ marginBottom: 16, opacity: 0.5 }}
                          />
                          <Text size="lg" fw={500} mb={8}>
                            No transactions found
                          </Text>
                          <Text size="sm" c="dimmed">
                            This student hasn't made any transactions yet.
                          </Text>
                        </Paper>
                      )}
                    </Stack>
                  </Card>
                </Tabs.Panel>

                {/* Assessments Tab */}
                <Tabs.Panel value="assessments" pt="lg">
                  <Card shadow="sm" padding="xl" radius="md" withBorder>
                    <Text fw={600} mb="lg">
                      Assessment History
                    </Text>
                    <Stack gap="md">
                      {assessments.map((assessment) => (
                        <Paper key={assessment.id} p="lg" withBorder>
                          <Group justify="space-between" align="flex-start">
                            <div>
                              <Text fw={600} mb={4}>
                                {assessment.title}
                              </Text>
                              <Group gap="lg" c="dimmed">
                                <Group gap={4}>
                                  <IconCalendar size={14} />
                                  <Text>
                                    Created:{" "}
                                    {dayjs(assessment.createdAt).format(
                                      "MMM DD, YYYY"
                                    )}
                                  </Text>
                                </Group>
                                <Group gap={4}>
                                  <IconBook size={14} />
                                  <Text>Course ID: {assessment.courseID}</Text>
                                </Group>
                                <Group gap={4}>
                                  <IconTestPipe size={14} />
                                  <Text>
                                    {assessment.questions.length} questions
                                  </Text>
                                </Group>
                              </Group>
                            </div>
                            <Group gap={8}>
                              <Badge
                                color={assessment.isActive ? "green" : "gray"}
                                variant="light"
                              >
                                {assessment.isActive ? "Active" : "Inactive"}
                              </Badge>
                              {assessment.studentAttempt && (
                                <Badge color="blue" variant="light">
                                  Attempted
                                </Badge>
                              )}
                            </Group>
                          </Group>
                          {assessment.studentAttempt && (
                            <>
                              <Divider my="md" />
                              <Group justify="space-between">
                                <Text size="sm" c="dimmed">
                                  Student Scores:{" "}
                                  {assessment.studentAttempt.scores.join(", ")}
                                </Text>
                                <Text size="sm" fw={500}>
                                  Average:{" "}
                                  {Math.round(
                                    assessment.studentAttempt.scores.reduce(
                                      (a, b) => a + b,
                                      0
                                    ) / assessment.studentAttempt.scores.length
                                  )}
                                  %
                                </Text>
                              </Group>
                            </>
                          )}
                        </Paper>
                      ))}
                      {assessments.length === 0 && (
                        <Paper
                          style={{
                            textAlign: "center",
                            padding: "48px 24px",
                            background: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: 12,
                          }}
                        >
                          <IconTestPipe
                            size={64}
                            style={{ marginBottom: 16, opacity: 0.5 }}
                          />
                          <Text size="lg" fw={500} mb={8}>
                            No assessments found
                          </Text>
                          <Text size="sm" c="dimmed">
                            This student hasn't taken any assessments yet.
                          </Text>
                        </Paper>
                      )}
                    </Stack>
                  </Card>
                </Tabs.Panel>

                {/* Courses Tab */}
                <Tabs.Panel value="courses" pt="lg">
                  <Card shadow="sm" padding="xl" radius="md" withBorder>
                    <Text fw={600} mb="lg">
                      Enrolled Courses
                    </Text>
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                      {courses.map((course) => (
                        <Paper key={course.id} p="lg" withBorder>
                          <Group
                            justify="space-between"
                            align="flex-start"
                            mb="md"
                          >
                            <ThemeIcon size={50} radius="md" color="blue">
                              <IconSchool size={24} />
                            </ThemeIcon>
                            <Badge variant="light" color="blue">
                              Plan: {course.planID}
                            </Badge>
                          </Group>
                          <Text fw={600} mb={4}>
                            {course.name}
                          </Text>
                          <Text size="sm" c="dimmed" lineClamp={2}>
                            Course ID: {course.id}
                          </Text>
                        </Paper>
                      ))}
                      {courses.length === 0 && (
                        <Paper
                          style={{
                            textAlign: "center",
                            padding: "48px 24px",
                            background: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: 12,
                          }}
                        >
                          <IconSchool
                            size={64}
                            style={{ marginBottom: 16, opacity: 0.5 }}
                          />
                          <Text size="lg" fw={500} mb={8}>
                            No courses enrolled
                          </Text>
                          <Text size="sm" c="dimmed">
                            This student hasn't enrolled in any courses yet.
                          </Text>
                        </Paper>
                      )}
                    </SimpleGrid>
                  </Card>
                </Tabs.Panel>
              </Tabs>
            </Container>
          ) : (
            <></>
          )}
        </>
      )}
    </div>
  );
}
